import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import { useUserValueDeleteMutation } from '@dieterApi/user/useUserValueDeleteMutation';
import { useMemo } from 'react';
import { useUserValuesData } from './useUserValuesData';
import { useUserWithCompany } from './useUserWithCompany';

const IGNORED_TASKS_KEY = 'ignoredTasks';

/**
 * Hook to manage ignored tasks state in company-specific PersistentState
 * Replaces the previous user-specific NavigationState approach
 */
export function useIgnoredTasks() {
  const { activeCompany } = useUserWithCompany();
  const { userValues } = useUserValuesData();

  const [addUserValue] = useUserValueAddMutation();
  const [deleteUserValue] = useUserValueDeleteMutation();

  // Extract ignored tasks from user values
  const ignoredTasks = useMemo(() => {
    const ignoredTasksMap: Record<string, boolean> = {};

    if (userValues) {
      userValues
        .filter((uv) => uv.key === IGNORED_TASKS_KEY)
        .forEach((uv) => {
          // The index is the task persistentId, value indicates if ignored
          if (uv.index) {
            ignoredTasksMap[uv.index] = uv.value === 'true';
          }
        });
    }

    return ignoredTasksMap;
  }, [userValues]);

  const setIgnored = async (taskPersistentId: string, ignored: boolean) => {
    if (!activeCompany?.id) return;

    if (ignored) {
      // Add to ignored tasks
      await addUserValue({
        variables: {
          key: IGNORED_TASKS_KEY,
          value: 'true',
          index: taskPersistentId,
        },
      });
    } else {
      await deleteUserValue({
        variables: {
          key: IGNORED_TASKS_KEY,
          index: taskPersistentId,
        },
      });
    }
  };

  const isIgnored = (taskPersistentId: string): boolean => {
    return ignoredTasks[taskPersistentId] === true;
  };

  return {
    ignoredTasks,
    setIgnored,
    isIgnored,
  };
}
